import React, {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GetCohortAttendees } from "../services/cohortService";
import { useSelector } from "react-redux";
import collapseIconLeft from "../assets/collapse.svg";
import collapseIconRight from "../assets/collapse-right.svg";
import gridArrow from "../assets/grid-arrow.svg";
import gridArrowFilled from "../assets/grid-arrow-filled.svg";
import { AppContext } from "../context";
import { RightHeaderComponent } from "../components/menu/Header";
import { useDailyEvents } from "../services/daily-utils/useDailyEvents";
import useEnableJoinBtn from "../hooks/useEnableJoinBtn";
import { useCohorts } from "../hooks/useCohorts";
import { useMyCohorts } from "../hooks/useMyCohorts";
import { useMyJoinedCohorts } from "../hooks/useMyJoinedCohorts";
import clsx from "clsx";
import CohortList from "../components/CohortList";
import CohortMessages from "../components/CohortMessages";
import CohortDetails from "../components/CohortDetails";
import CohortAttachments from "../components/CohortAttachments";
import CohortRecordings from "../components/CohortRecordings";
import { Tooltip } from "antd";

function Cohort() {
  const { startLeavingCall, callObject, showCall, startHairCheck, joinCall } =
    useDailyEvents();
  const { setMeetingPayload, meetingPayload } = useContext(AppContext);
  const qalortUser = useSelector((state) => state.user.userData);

  const { data: cohorts = [], isLoading: loading } = useMyCohorts();
  const { data: registeredCohorts = [], isLoading: registeredLoading } =
    useMyJoinedCohorts();

  const [attendees, setAttendees] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  const [whatTab, setWhatTab] = useState("Post");
  const { slug } = useParams();
  const [mentionValues, setMentionValues] = useState([]);
  const navigate = useNavigate();
  const currentCohort = useMemo(() => {
    const currentItem = cohorts?.find((item) => item.slug === slug);
    return currentItem;
  }, [slug, cohorts]);

  const { disableJoinBtn } = useEnableJoinBtn(currentCohort);

  const onMouseEnter = () => {
    currentCohort?.meeting_link &&
      !disableJoinBtn &&
      startHairCheck(
        currentCohort.meeting_link,
        currentCohort.token,
        currentCohort
      );
  };

  useEffect(() => {
    if (!disableJoinBtn) {
      onMouseEnter();
    }
  }, [disableJoinBtn]);

  const onClick = () => {
    if (currentCohort.meeting_link && meetingPayload.roomUrl) {
      joinCall(currentCohort.meeting_link);
    }
  };

  const getAtendees = async (id) => {
    setAttendees([]);
    try {
      const res = await GetCohortAttendees(id);
      setAttendees(res.data.data || []);
    } catch (error) {
      setAttendees([]);
    }
  };

  useEffect(() => {
    if (currentCohort) {
      getAtendees(currentCohort.id);
    }
  }, [currentCohort]);

  useEffect(() => {
    if (showCall) {
      setMeetingPayload({
        meetingInfo: currentCohort,
        showCall,
        callObject,
        startLeavingCall,
      });
    }
  }, [showCall, callObject]);

  const createMessageData = ({ content, files }) => {
    return {
      category: "post",
      cohort_id: currentCohort?.id,
      title: "public announcement",
      message: content,
      files: files,
    };
  };

  useEffect(() => {
    if (attendees.length > 0) {
      const transformedAttendees = attendees.map((attendee) => ({
        id: attendee?.user?.id,
        value: attendee?.user?.name,
      }));

      setMentionValues(transformedAttendees);
    }
  }, [attendees]);

  return (
    <>
      <div
        className={clsx("grid transition-all duration-150", {
          "grid-cols-[250px_1fr]": !collapsed,
          "grid-cols-[65px_1fr]": collapsed,
        })}
      >
        <div
          className={clsx("h-screen border-r border-[#ECEBF5]", {
            "!w-[65px] overflow-x-hidden": collapsed,
          })}
        >
          <div className="h-[90px] border-b border-[#ECEBF5] bg-[#F9F9FF] [@media(max-width:984px)]:relative [@media(max-width:984px)]:top-[89px] [@media(max-width:984px)]:border-t [@media(max-width:984px)]:border-[#ECEBF5]">
            <div className="flex justify-between px-4">
              {!collapsed && (
                <div className="pr-2.5 flex-col pt-4">
                  <h3 className="text-[#202053] text-[18px] mb-1 -mt-1">
                    All Cohorts
                  </h3>
                  <p className="text-[14px] text-[#535393] one-liner -mt-2 mb-0">
                    You can find all your Cohorts here
                  </p>
                </div>
              )}
              <div className="h-[90px] flex items-center cursor-pointer">
                <Tooltip
                  title={
                    <p className="text-main">
                      {collapsed ? "Expand" : "Collapse"} sidebar
                    </p>
                  }
                  placement="bottomLeft"
                >
                  <img
                    src={collapsed ? collapseIconRight : collapseIconLeft}
                    alt=""
                    onClick={() => setCollapsed(!collapsed)}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="flex-1 h-[calc(100vh-90px)] [@media(max-width:984px)]:h-[calc(100vh-180px)] [@media(max-width:984px)]:mt-[90px] overflow-hidden relative">
            <div className="h-[60px] border-b border-[#ECEBF5] px-2 flex items-center">
              <Tooltip
                title={<p className="text-main">View all cohorts</p>}
                placement="bottomLeft"
              >
                <button
                  className={clsx("p-2 flex items-center", {
                    "border-r border-[#ECEBF5]": !collapsed,
                  })}
                  onClick={() => navigate("/cohort")}
                >
                  <img src={collapsed ? gridArrowFilled : gridArrow} alt="" />
                </button>
              </Tooltip>
              {!collapsed && (
                <p className="text-[#30307D] font-semibold m-0 pl-2 text-[18px]">
                  Cohorts
                </p>
              )}
            </div>

            <CohortList collapsed={collapsed} setCollapsed={setCollapsed} />
          </div>
        </div>
        <div className="w-full">
          <div className="h-[90px] border-b border-[#ECEBF5] bg-[#F9F9FF] flex justify-end px-[40px] [@media(max-width:984px)]:px-4 [@media(max-width:984px)]:flex [@media(max-width:984px)]:items-center [@media(max-width:984px)]:justify-between">
            <p className="[@media(max-width:984px)]:block hidden text-main font-semibold mb-0">
              Cohorts
            </p>
            <RightHeaderComponent />
          </div>
          <div className="flex-1 h-[calc(100vh-90px)] overflow-hidden">
            <CohortDetails
              currentCohort={currentCohort}
              defaultTab={whatTab}
              onChangeTab={setWhatTab}
              onClick={onClick}
            />

            {whatTab === "Post" && (
              <CohortMessages
                currentCohort={currentCohort}
                qalortUser={qalortUser}
                createMessageData={createMessageData}
                mentionValues={mentionValues}
              />
            )}

            {whatTab === "Attachment" && (
              <CohortAttachments
                currentCohort={currentCohort}
                qalortUser={qalortUser}
                createMessageData={createMessageData}
                mentionValues={mentionValues}
              />
            )}

            {whatTab === "Recordings" && (
              <CohortRecordings
                currentCohort={currentCohort}
                qalortUser={qalortUser}
                createMessageData={createMessageData}
                mentionValues={mentionValues}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Cohort;
